<app-modal
  [title]="isEditMode() ? 'Edit Program' : 'Create New Program'"
  [subtitle]="
    isEditMode()
      ? 'Update program settings and configuration.'
      : 'Configure recording rules and program-specific data elements.'
  "
  (closed)="closed.emit()"
>
  <!-- ── Row 1: Program Name + Program Type ── -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
    <div class="flex flex-col gap-1.5">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Program Name</label>
      <input
        type="text"
        placeholder="Type name..."
        [value]="programName()"
        (input)="programName.set($any($event.target).value)"
        [class.border-red-400]="showErrors() && !programName().trim()"
        [class.ring-2]="showErrors() && !programName().trim()"
        [class.ring-red-100]="showErrors() && !programName().trim()"
        class="w-full"
      />
      @if (showErrors() && !programName().trim()) {
        <span class="text-2xs text-red-500">Required</span>
      }
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Program Type</label>
      <app-select
        placeholder="Select type"
        [options]="programTypeOptions()"
        [value]="selectedProgramTypeId()"
        [error]="showErrors()"
        (changed)="selectedProgramTypeId.set($event)"
      />
      @if (showErrors() && !selectedProgramTypeId()) {
        <span class="text-2xs text-red-500">Required</span>
      }
    </div>
  </div>

  <!-- ── Row 2: Substantive Area + Response Taxonomy ── -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
    @if (isRecordingType()) {
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-semibold text-navy-600 tracking-wider">Division</label>
        <app-select
          placeholder="Select area"
          [options]="substantiveAreaOptions()"
          [value]="selectedSubstantiveAreaId()"
          [error]="showErrors()"
          (changed)="selectedSubstantiveAreaId.set($event)"
        />
        @if (showErrors() && !selectedSubstantiveAreaId()) {
          <span class="text-2xs text-red-500">Required</span>
        }
      </div>
    }

    @if (isRecordingType()) {
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-semibold text-navy-600 tracking-wider">Response Taxonomy</label>
        <app-select
          placeholder="Select taxonomy"
          [options]="responseTaxonomyOptions()"
          [value]="selectedResponseTaxonomyId()"
          [error]="showErrors()"
          (changed)="selectedResponseTaxonomyId.set($event)"
        />
        @if (showErrors() && !selectedResponseTaxonomyId()) {
          <span class="text-2xs text-red-500">Required</span>
        }
      </div>
      <!-- Response Category Usage (read-only, derived from taxonomy) -->
      <div>
        <p class="text-xs font-bold text-navy-900 mb-3">Response Category Usage</p>
        <div class="flex flex-col gap-2.5">
          <label class="flex items-center gap-3">
            <div
              class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
              [ngClass]="
                selectedResponseTaxonomy()?.isMultiItem === true
                  ? 'border-accent-500 bg-accent-500'
                  : 'border-surface-300 bg-white'
              "
            >
              @if (selectedResponseTaxonomy()?.isMultiItem === true) {
                <div class="w-2 h-2 rounded-full bg-white"></div>
              }
            </div>
            <span class="text-xs text-navy-900">Multiple categories per Incident</span>
          </label>
          <label class="flex items-center gap-3">
            <div
              class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
              [ngClass]="
                selectedResponseTaxonomy()?.isMultiItem === false
                  ? 'border-accent-500 bg-accent-500'
                  : 'border-surface-300 bg-white'
              "
            >
              @if (selectedResponseTaxonomy()?.isMultiItem === false) {
                <div class="w-2 h-2 rounded-full bg-white"></div>
              }
            </div>
            <span class="text-xs text-navy-900">One category only per Investigation</span>
          </label>
        </div>
      </div>
    }
    <!-- ── Row 3: Situation Taxonomy ── -->
    <div class="flex flex-col gap-1.5">
      <label class="text-xs font-semibold text-navy-600 tracking-wider">Situation Taxonomy</label>
      <app-select
        placeholder="Select taxonomy"
        [options]="situationTaxonomyOptions()"
        [value]="selectedSituationTaxonomyId()"
        [error]="showErrors()"
        (changed)="selectedSituationTaxonomyId.set($event)"
      />
      @if (showErrors() && !selectedSituationTaxonomyId()) {
        <span class="text-2xs text-red-500">Required</span>
      }
    </div>
    <!-- Situation Category Usage (read-only, derived from taxonomy) -->
    <div>
      <p class="text-xs font-bold text-navy-900 mb-3">Situation Category Usage</p>
      <div class="flex flex-col gap-2.5">
        <label class="flex items-center gap-3">
          <div
            class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
            [ngClass]="
              selectedSituationTaxonomy()?.isMultiItem === true
                ? 'border-accent-500 bg-accent-500'
                : 'border-surface-300 bg-white'
            "
          >
            @if (selectedSituationTaxonomy()?.isMultiItem === true) {
              <div class="w-2 h-2 rounded-full bg-white"></div>
            }
          </div>
          <span class="text-xs text-navy-900">Multiple categories per Incident</span>
        </label>
        <label class="flex items-center gap-3">
          <div
            class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
            [ngClass]="
              selectedSituationTaxonomy()?.isMultiItem === false
                ? 'border-accent-500 bg-accent-500'
                : 'border-surface-300 bg-white'
            "
          >
            @if (selectedSituationTaxonomy()?.isMultiItem === false) {
              <div class="w-2 h-2 rounded-full bg-white"></div>
            }
          </div>
          <span class="text-xs text-navy-900">One category only per Incident</span>
        </label>
      </div>
    </div>
  </div>

  <!-- ── Recording-only fields ── -->
  @if (isRecordingType()) {
    <!-- ── Program Locations ── -->
    <div class="mb-6">
      <p class="text-xs font-bold text-navy-900 mb-3">Program Locations</p>
      <div class="flex flex-col gap-2">
        @for (loc of locations(); track loc.locationId) {
          <div
            class="flex items-center gap-3 px-4 py-3 rounded-xl border border-surface-300 bg-white group"
          >
            <img src="/icons/location-pin-icon.svg" class="w-4 h-4 shrink-0" alt="" />
            <span class="flex-1 min-w-0 truncate text-xs text-navy-900">{{ loc.name }}</span>
            <div class="relative shrink-0 flex items-center">
              <button
                type="button"
                (click)="
                  openStatusLocationId.set(
                    openStatusLocationId() === loc.locationId ? null : loc.locationId
                  )
                "
                (blur)="closeLocationStatus()"
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-2xs font-semibold border cursor-pointer transition-colors"
                [ngClass]="
                  loc.status === 'Active'
                    ? 'bg-green-50 text-green-600 border-green-200'
                    : 'bg-red-50 text-red-500 border-red-200'
                "
              >
                {{ loc.status }}
                <img
                  src="/icons/chevron-down-icon.svg"
                  class="w-2.5 h-2.5 transition-transform"
                  [ngClass]="openStatusLocationId() === loc.locationId ? 'rotate-180' : ''"
                  alt=""
                />
              </button>
              @if (openStatusLocationId() === loc.locationId) {
                <div
                  class="absolute right-0 top-full mt-1 bg-white rounded-xl border border-surface-300 shadow-lg overflow-hidden z-200 py-1 min-w-28"
                >
                  <button
                    type="button"
                    (mousedown)="setLocationStatus(loc.locationId, 'Active')"
                    class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    (mousedown)="setLocationStatus(loc.locationId, 'Inactive')"
                    class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                  >
                    Inactive
                  </button>
                </div>
              }
            </div>
            <button
              type="button"
              (click)="removeLocation(loc.locationId)"
              class="w-5 h-5 flex items-center justify-center rounded-full hover:bg-surface-200 transition-colors cursor-pointer shrink-0 opacity-0 group-hover:opacity-100"
            >
              <img src="/icons/close-icon.svg" class="w-3 h-3" alt="Remove" />
            </button>
          </div>
        }

        <!-- Add location row -->
        <div
          class="flex items-center gap-3 px-4 py-3 rounded-xl border h-12"
          [ngClass]="
            showErrors() && !locations().length
              ? 'border-red-400 ring-2 ring-red-100'
              : 'border-surface-400'
          "
        >
          <img src="/icons/location-pin-icon.svg" class="w-4 h-4 shrink-0 opacity-50" alt="" />

          <!-- Autocomplete input -->
          <div class="relative flex-1">
            <input
              type="text"
              placeholder="Type location name..."
              [value]="locationSearch()"
              (input)="
                locationSearch.set($any($event.target).value); locationDropdownOpen.set(true)
              "
              (focus)="locationDropdownOpen.set(true)"
              (blur)="closeLocationDropdown()"
              class="w-full border-0 bg-transparent p-0 text-xs text-navy-900 placeholder:text-slate-400 focus:ring-0 focus:outline-none"
            />
            @if (locationNameWarning()) {
              <div
                class="absolute top-full left-0 mt-1 z-200 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-2xs text-amber-600 whitespace-nowrap"
              >
                This location has already been added
              </div>
            } @else if (locationDropdownOpen() && filteredLocationOptions().length) {
              <div
                class="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-surface-300 shadow-lg overflow-hidden z-200 py-1 overflow-y-auto h-40"
              >
                @for (opt of filteredLocationOptions(); track opt.value) {
                  <button
                    type="button"
                    (mousedown)="selectLocation(opt.value)"
                    class="w-full text-left px-4 py-2.5 text-xs text-navy-900 hover:bg-surface-100 transition-colors cursor-pointer"
                  >
                    {{ opt.label }}
                  </button>
                }
              </div>
            }
          </div>

          <!-- Set Status dropdown -->
          <div class="relative shrink-0 flex items-center">
            <button
              type="button"
              (click)="newStatusDropdownOpen.set(!newStatusDropdownOpen())"
              (blur)="closeStatusDropdown()"
              class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-2xs font-semibold border cursor-pointer transition-colors"
              [ngClass]="
                newLocationStatus() === 'Active'
                  ? 'bg-green-50 text-green-600 border-green-200'
                  : 'bg-red-50 text-red-500 border-red-200'
              "
            >
              {{ newLocationStatus() }}
              <img
                src="/icons/chevron-down-icon.svg"
                class="w-2.5 h-2.5 transition-transform"
                [ngClass]="newStatusDropdownOpen() ? 'rotate-180' : ''"
                alt=""
              />
            </button>
            @if (newStatusDropdownOpen()) {
              <div
                class="absolute right-0 top-full mt-1 bg-white rounded-xl border border-surface-300 shadow-lg overflow-hidden z-200 py-1 min-w-28"
              >
                <button
                  type="button"
                  (mousedown)="newLocationStatus.set('Active'); newStatusDropdownOpen.set(false)"
                  class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                >
                  Active
                </button>
                <button
                  type="button"
                  (mousedown)="newLocationStatus.set('Inactive'); newStatusDropdownOpen.set(false)"
                  class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                >
                  Inactive
                </button>
              </div>
            }
          </div>
          <span class="w-5 shrink-0"></span>
        </div>
        @if (showErrors() && !locations().length) {
          <span class="text-2xs text-red-500">Required</span>
        }
      </div>
    </div>
  }
  <!-- end @if isRecordingType -->

  <!-- ── Data Elements ── -->
  <div class="mb-2">
    <p class="text-xs font-bold mb-3 text-navy-900">Data elements</p>
    <div class="flex flex-col gap-2">
      @for (el of dataElements(); track el.id; let i = $index) {
        <div
          draggable="true"
          (dragstart)="onDragStart(i)"
          (dragover)="onDragOver($event, i)"
          (drop)="onDrop(i)"
          (dragend)="onDragEnd()"
          class="flex flex-col px-4 pt-2.5 pb-2.5 rounded-xl border bg-white group transition-colors"
          [class.border-accent-400]="dropTarget() === i && dragIndex() !== i"
          [class.border-surface-300]="dropTarget() !== i || dragIndex() === i"
          [class.opacity-40]="dragIndex() === i"
        >
          <div class="flex flex-wrap items-center gap-x-4 gap-y-4 md:gap-2">
            <span class="text-xs text-slate-400 w-5 shrink-0 text-right">{{ i + 1 }}.</span>
            <img src="/icons/drag-handle-icon.svg" class="w-4 h-4 shrink-0 cursor-grab" alt="" />
            <div
              class="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-100 shrink-0"
            >
              <img src="/icons/data-elements-icon.svg" class="w-4 h-4" alt="" />
            </div>
            <span class="flex-1 min-w-0 text-xs text-navy-900 truncate">{{ el.name }}</span>
            <!-- En mobile ocupa fila propia con indent; en desktop queda inline -->
            <div class="w-full md:w-auto flex items-center gap-3 pl-3 md:pl-0">
              <app-date-range-picker
                variant="compact"
                [dateFrom]="el.beginDate"
                [dateTo]="el.endDate ?? ''"
                [error]="showErrors() && !el.beginDate"
                (rangeChange)="updateDataElementDates(el.id, $event)"
              />
              <button
                type="button"
                (click)="removeDataElement(el.id)"
                class="w-5 h-5 flex items-center justify-center rounded-full hover:bg-surface-200 transition-colors cursor-pointer shrink-0 opacity-0 group-hover:opacity-100"
              >
                <img src="/icons/close-icon.svg" class="w-3 h-3" alt="Remove" />
              </button>
            </div>
          </div>

          <div class="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mt-2 pl-3 pr-8">
            @if (isRecordingType()) {
              <!-- Level -->
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-slate-400">Level</span>
                <div class="relative shrink-0 flex items-center">
                  <button
                    type="button"
                    (click)="openLevelElementId.set(openLevelElementId() === el.id ? null : el.id)"
                    (blur)="closeLevelDropdown()"
                    class="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-xs text-slate-500 hover:text-navy-900 transition-colors cursor-pointer"
                  >
                    {{ el.levelIncidentPI === 0 ? 'Incident' : 'PI' }}
                    <img
                      src="/icons/chevron-down-icon.svg"
                      class="w-3 h-3 shrink-0 transition-transform"
                      [ngClass]="openLevelElementId() === el.id ? 'rotate-180' : ''"
                      alt=""
                    />
                  </button>
                  @if (openLevelElementId() === el.id) {
                    <div
                      class="absolute left-0 top-full mt-1 bg-white rounded-xl border border-surface-300 shadow-lg overflow-hidden z-200 py-1 min-w-24"
                    >
                      <button
                        type="button"
                        (mousedown)="updateDataElementLevel(el.id, 0); openLevelElementId.set(null)"
                        class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                      >
                        Incident
                      </button>
                      <button
                        type="button"
                        (mousedown)="updateDataElementLevel(el.id, 1); openLevelElementId.set(null)"
                        class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                      >
                        PI
                      </button>
                    </div>
                  }
                </div>
              </div>
              <!-- PI Type -->
              @if (el.levelIncidentPI === 1) {
                <div class="flex items-center gap-1.5">
                  <span class="text-xs text-slate-400">PI Type</span>
                  <div class="relative shrink-0 flex items-center">
                    <button
                      type="button"
                      (click)="
                        openPITypeElementId.set(openPITypeElementId() === el.id ? null : el.id)
                      "
                      (blur)="closePITypeDropdown()"
                      class="inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-xs text-slate-500 hover:text-navy-900 transition-colors cursor-pointer"
                    >
                      {{ piTypeLabelFor(el.piInvolvementTypeId) }}
                      <img
                        src="/icons/chevron-down-icon.svg"
                        class="w-3 h-3 shrink-0 transition-transform"
                        [ngClass]="openPITypeElementId() === el.id ? 'rotate-180' : ''"
                        alt=""
                      />
                    </button>
                    @if (openPITypeElementId() === el.id) {
                      <div
                        class="absolute right-0 bottom-full mb-1 bg-white rounded-xl border border-surface-300 shadow-lg overflow-hidden z-200 py-1 min-w-32"
                      >
                        <button
                          type="button"
                          (mousedown)="
                            updateDataElementPIType(el.id, null); openPITypeElementId.set(null)
                          "
                          class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                        >
                          All
                        </button>
                        @for (opt of personTypeOptions(); track opt.value) {
                          <button
                            type="button"
                            (mousedown)="
                              updateDataElementPIType(el.id, opt.value);
                              openPITypeElementId.set(null)
                            "
                            class="w-full text-left px-4 py-2 text-xs text-navy-900 hover:bg-surface-100 cursor-pointer"
                          >
                            {{ opt.label }}
                          </button>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
            }
            <!-- Required — pushed to the right on desktop -->
            <label class="flex items-center gap-1.5 cursor-pointer select-none md:ml-auto">
              <input
                type="checkbox"
                [checked]="!!el.requiredOptional"
                (change)="updateDataElementRequired(el.id, $any($event.target).checked)"
                class="w-3.5 h-3.5 rounded cursor-pointer accent-accent-500"
              />
              <span class="text-xs text-slate-500">Required</span>
            </label>
          </div>
        </div>
      }

      <!-- Empty / add row -->
      <div
        class="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-dashed border-surface-400"
      >
        <span class="text-xs text-slate-400 w-5 shrink-0 text-right">{{
          dataElements().length + 1
        }}</span>
        <img src="/icons/drag-handle-icon.svg" class="w-4 h-4 shrink-0 opacity-40" alt="" />
        <div class="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-100 shrink-0">
          <img src="/icons/data-elements-icon.svg" class="w-4 h-4 opacity-50" alt="" />
        </div>
        <div class="relative flex-1 flex items-center">
          <button
            type="button"
            (click)="toggleAddElementDropdown($event)"
            (blur)="closeAddElementDropdown()"
            class="flex items-center justify-between w-full bg-transparent border-0 p-0 text-xs text-slate-400 hover:text-navy-900 transition-colors cursor-pointer"
          >
            <span>Select Element</span>
            <img
              src="/icons/chevron-down-icon.svg"
              class="w-3 h-3 shrink-0 transition-transform"
              [ngClass]="openAddElementDropdown() ? 'rotate-180' : ''"
              alt=""
            />
          </button>
          @if (openAddElementDropdown()) {
            <div
              class="fixed z-200 bg-white rounded-xl border border-surface-300 shadow-lg overflow-hidden overflow-y-auto max-h-40 py-1"
              [style.top]="addElementDropdownPos()?.top ?? null"
              [style.bottom]="addElementDropdownPos()?.bottom ?? null"
              [style.left]="addElementDropdownPos()?.left"
              [style.width]="addElementDropdownPos()?.width"
            >
              @for (opt of configuredDataElementOptions(); track opt.value) {
                @if (!dataElements().some((el) => el.id === opt.value)) {
                  <button
                    type="button"
                    (mousedown)="selectDataElement(opt.value)"
                    class="w-full text-left px-4 py-2.5 text-xs text-navy-900 hover:bg-surface-100 transition-colors cursor-pointer"
                  >
                    {{ opt.label }}
                  </button>
                }
              }
            </div>
          }
        </div>
        <span class="w-5 shrink-0"></span>
      </div>
    </div>
  </div>

  <!-- Footer -->
  <div modal-footer class="flex items-center justify-between gap-3">
    @if (isEditMode()) {
      @if (confirmRemove()) {
        <div class="flex items-center gap-2">
          <span class="text-xs text-slate-500">Delete this program?</span>
          <button
            type="button"
            (click)="onRemove()"
            [disabled]="removing()"
            class="px-3 py-1.5 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ removing() ? 'Deleting...' : 'Confirm' }}
          </button>
          <button
            type="button"
            (click)="confirmRemove.set(false)"
            [disabled]="removing()"
            class="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      } @else {
        <button
          type="button"
          (click)="onRemove()"
          class="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-600 transition-colors cursor-pointer"
        >
          <img src="/icons/trash-icon.svg" class="w-4 h-4" alt="" />
          Delete program
        </button>
      }
    } @else {
      <span></span>
    }

    <div class="flex items-center gap-3">
      <button
        type="button"
        (click)="closed.emit()"
        class="px-6 py-2.5 text-sm font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer"
      >
        Cancel
      </button>
      <button
        type="button"
        (click)="onSubmit()"
        [disabled]="submitting()"
        class="px-6 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ submitting() ? 'Saving...' : isEditMode() ? 'Save Changes' : 'Create Program' }}
      </button>
    </div>
  </div>
</app-modal>
